import { Alert, Box, Link, Typography } from '@mui/material';
import { useEffect, useMemo, useRef } from 'react';
import type { FieldErrors } from 'react-hook-form';
import type { FormDescriptor, FormErrorDescriptor } from '@finance/contracts';
import { useAppI18n } from '../../i18n/use-app-i18n';
import { translateRuntimeTKeyOrFallback } from '../../i18n/resolve-key';

export interface ErrorSummaryProps {
  fieldErrors: FieldErrors<Record<string, unknown>>;
  globalErrors?: FormErrorDescriptor[];
  submissionError?: FormErrorDescriptor | null;
  descriptor?: FormDescriptor;
  /** When true, the summary focuses itself (after a failed submit). */
  focusOnRender?: boolean;
}

function isGlobalErrorKey(field: string): boolean {
  return field === '__global' || field === '_global';
}

export function FormErrorSummary({ fieldErrors, globalErrors = [], submissionError, descriptor, focusOnRender = false }: ErrorSummaryProps) {
  const alertRef = useRef<HTMLDivElement>(null);
  const { text, translator } = useAppI18n();

  const fieldByName = useMemo(() => {
    const map = new Map<string, { label: string; name: string }>();
    if (!descriptor) return map;
    for (const field of descriptor.fields) {
      const label = text(field.labelKey);
      map.set(field.name, { name: field.name, label });
      map.set(field.id as string, { name: field.name, label });
    }
    return map;
  }, [descriptor, text]);

  const fieldErrorList: { field: string; label: string; message: string; isGlobal: boolean }[] = [];
  const seenFields = new Set<string>();

  // React Hook Form messages have already crossed the validation translator
  // boundary. They are display text, not runtime translation keys — except
  // for server errors, which store the canonical translation key as the RHF
  // message and must be translated before presentation.
  for (const [fieldName, error] of Object.entries(fieldErrors)) {
    if (error?.message) {
      const message = error.type === 'server'
        ? translateRuntimeTKeyOrFallback(translator, String(error.message))
        : String(error.message);
      const key = `${fieldName}:${message}`;
      if (!seenFields.has(key)) {
        seenFields.add(key);
        const isGlobal = isGlobalErrorKey(fieldName);
        const label = isGlobal ? message : (fieldByName.get(fieldName)?.label ?? fieldName);
        fieldErrorList.push({ field: fieldName, label, message, isGlobal });
      }
    }
  }

  // FormErrorDescriptor.code is currently a canonical TranslationKey contract.
  // Translate it as such instead of guessing whether an arbitrary string is a key.
  for (const err of globalErrors) {
    const msg = text(err.code, { ...(err.params ?? {}) });
    const suffix = err.correlationId ? ` (${err.correlationId})` : '';
    const key = `__global:${msg}:${suffix}`;
    if (!seenFields.has(key)) {
      seenFields.add(key);
      fieldErrorList.push({ field: '__global', label: msg, message: `${msg}${suffix}`, isGlobal: true });
    }
  }

  if (submissionError && globalErrors.length === 0) {
    const msg = text(submissionError.code, { ...(submissionError.params ?? {}) });
    fieldErrorList.push({ field: '__global', label: msg, message: msg, isGlobal: true });
  }

  const translatedErrors = fieldErrorList.map(({ field, label, message, isGlobal }) => ({
    field,
    isGlobal,
    message: `${isGlobal ? '' : `${label}: `}${message}`,
  }));

  useEffect(() => {
    if (!focusOnRender || translatedErrors.length === 0) return;
    const firstField = translatedErrors.find(error => !error.isGlobal)?.field;
    if (firstField) {
      const input = document.querySelector(`[name="${CSS.escape(firstField)}"]`) as HTMLElement | null;
      if (input) {
        input.focus();
        return;
      }
    }
    alertRef.current?.focus();
  }, [focusOnRender, translatedErrors.length]);

  if (translatedErrors.length === 0) return null;

  const handleFieldClick = (fieldName: string) => {
    if (typeof CSS !== 'undefined' && 'escape' in CSS) {
      const selector = `[name="${CSS.escape(fieldName)}"]`;
      const input = document.querySelector(selector) as HTMLElement | null;
      if (input) {
        input.focus();
        return;
      }
    }
    const input = document.querySelector(`[name="${fieldName}"]`) as HTMLElement | null;
    if (input) input.focus();
  };

  return (
    <Alert
      ref={alertRef}
      severity="error"
      sx={{
        mb: 2,
        minWidth: 0,
        '& .MuiAlert-message': {
          minWidth: 0,
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        },
      }}
      role="alert"
      aria-live="assertive"
      tabIndex={-1}
    >
      <Box component="ul" sx={{ m: 0, pl: 2 }}>
        {translatedErrors.map(({ field, message, isGlobal }, idx) => (
          <li key={`${field}-${idx}`}>
            {isGlobal ? (
              <Typography component="span">{message}</Typography>
            ) : (
              <Link
                component="button"
                type="button"
                onClick={() => handleFieldClick(field)}
                sx={{ textDecoration: 'none', color: 'inherit', textAlign: 'left' }}
              >
                {message}
              </Link>
            )}
          </li>
        ))}
      </Box>
    </Alert>
  );
}
